import React, { useEffect } from 'react';
import querystring from 'query-string';
import i18n from 'i18next';
import { Trans } from 'react-i18next';
import { Box, Typography } from '@material-ui/core';
import { fever500, neutral700 } from '@fever/ui';
import LayoutInner from 'features/login/components/LayoutInner';
import useFreshChat from 'hooks/useFreshChat';
import { getRedirectUrlWithFallbackSponsor } from 'api/commom';
import { generateLoginUrl } from 'utils/login';
import {
    permissionDeniedReasons,
    isKnownPermissionDeniedReason
} from 'config/permissionDeniedReasons';
import { sentryCaptureException } from 'utils/sentry';
import { generateApiErrObj } from 'features/webhooks/useApiErrorHandler';
import { UNEXPECTED_PERMISSION_DENIED_REDIRECT } from 'config/errorTypes';

// 每個白名單 reason 對應的文案。沒列在這裡的 reason 走 defaultCopy。
const reasonCopy = {
    [permissionDeniedReasons.SPONSOR_NOT_FOUND]: {
        title: 'permission_denied.sponsor_not_found_title',
        desc: 'permission_denied.sponsor_not_found_desc'
    },
    [permissionDeniedReasons.SPONSOR_NOT_EXIST]: {
        title: 'permission_denied.sponsor_not_exist_title',
        desc: 'permission_denied.sponsor_not_exist_desc'
    },
    [permissionDeniedReasons.ROLE_NO_PERMISSION]: {
        title: 'permission_denied.title',
        desc: 'permission_denied.role_no_permission_desc'
    },
    [permissionDeniedReasons.MISSING_SPONSOR_ID]: {
        title: 'permission_denied.missing_sponsor_id_title',
        desc: 'permission_denied.missing_sponsor_id_desc'
    },
    [permissionDeniedReasons.ROUTE_NOT_ALLOWED]: {
        title: 'permission_denied.title',
        desc: 'permission_denied.route_not_allowed_desc'
    },
    [permissionDeniedReasons.NO_POINT_POOL]: {
        title: 'permission_denied.no_point_pool_title',
        desc: 'permission_denied.no_point_pool_desc'
    },
    [permissionDeniedReasons.FORM_UNPUBLISHED]: {
        title: 'permission_denied.form_unpublished_title',
        desc: 'permission_denied.form_unpublished_desc'
    },
    [permissionDeniedReasons.FORM_TEST_EXPIRED]: {
        title: 'permission_denied.form_test_expired_title',
        desc: 'permission_denied.form_test_expired_desc'
    },
    [permissionDeniedReasons.FORM_INVALID_NAME]: {
        title: 'permission_denied.form_invalid_name_title',
        desc: 'permission_denied.form_invalid_name_desc'
    }
};

const defaultCopy = {
    title: 'permission_denied.title',
    desc: 'permission_denied.desc'
};

export default function PagePermissionDenied() {
    const { open: openFreshChat = () => {} } = useFreshChat();
    // reason 走白名單：只認 permissionDeniedReasons 內的值。
    // 未知值 / 未帶 reason 一律顯示預設「無使用權限」文案 —— 絕不把 URL 上的字串回填畫面。
    const { reason, refUrl } = querystring.parse(window.location.search);
    const isKnownReason = isKnownPermissionDeniedReason(reason);
    const { title, desc } = isKnownReason ? reasonCopy[reason] ?? defaultCopy : defaultCopy;

    useEffect(() => {
        // 白名單 reason 是已知情境，使用者從畫面與網址就看得出原因，不需要 Sentry。
        // 只有「未帶 reason」或「未知 reason」才是未預期的導轉 —— 那才需要追。
        if (isKnownReason) return;
        try {
            throw generateApiErrObj(
                {
                    reason: reason ?? null,
                    refUrl: refUrl ?? null,
                    referrer: document.referrer || null,
                    landedUrl: window.location.href
                },
                UNEXPECTED_PERMISSION_DENIED_REDIRECT
            );
        } catch (error) {
            sentryCaptureException(error);
        }
        // 只在掛載時判斷一次；reason 來自 URL，同一次掛載內不會變
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClick = e => {
        e.preventDefault();
        openFreshChat();
    };
    return (
        <LayoutInner showMenu={false}>
            <Box>
                <Box
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24,
                        marginBottom: 96,
                        textAlign: 'center'
                    }}
                >
                    <Typography variant="h5">{i18n.t(title)}</Typography>
                    <Typography
                        variant="subtitle2"
                        style={{ textAlign: 'center', color: neutral700 }}
                    >
                        {i18n.t(desc)}
                    </Typography>
                </Box>
                <Box style={{ textAlign: 'center', color: neutral700 }}>
                    <Typography variant="subtitle2">
                        <Trans
                            i18nKey="permission_denied.contact_link"
                            components={[
                                <span
                                    css={{ color: fever500, cursor: 'pointer' }}
                                    onClick={handleClick}
                                >
                                    0
                                </span>
                            ]}
                        />
                    </Typography>
                    <Typography variant="subtitle2">
                        <Trans
                            i18nKey="permission_denied.create_sponsor"
                            components={[
                                <a
                                    href={generateLoginUrl(getRedirectUrlWithFallbackSponsor())}
                                    rel="noopener noreferrer"
                                    css={{ color: fever500 }}
                                >
                                    0
                                </a>
                            ]}
                        />
                    </Typography>
                </Box>
            </Box>
        </LayoutInner>
    );
}
